/**
 * 四象限散点图 - 图表组件
 */
import React, { useContext, useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import { DBO_EC, DBO_TM, DBO_V } from '@op/utils/format';
import { getFilterDateQOQText } from '@op/utils/time';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import iconUpGreen from '@op/assets/svg/icon-up-green.svg';
import iconDownRed from '@op/assets/svg/icon-down-red.svg';
import { CATEGORY_CONFIG, BUBBLE_SIZE, QUADRANT_CONFIG } from '../../constants';
import { calculateBubbleSize } from '../../utils/calculations';
import type { IProcessedQuadrantItem } from '../../hooks/useQuadrantData';
import { SecurityWeeklyReportContext } from '../../context';

interface IQuadrantScatterChartProps {
  processedData: IProcessedQuadrantItem[];
  thresholds: {
    x_axis: number;
    y_axis: number;
  };
  linkTime: {
    start_time: string;
    end_time: string;
  } | null;
  filter: any;
  height?: number;
  categoryList: string[];
  categoryCnMap: Map<string, string>;
}

const QuadrantScatterChart: React.FC<IQuadrantScatterChartProps> = ({
  processedData,
  thresholds,
  linkTime,
  filter,
  height = 550,
  categoryList,
  categoryCnMap,
}) => {
  const { formatMessage, isEn } = useLanguage();
  const { toFeeds } = useContext(SecurityWeeklyReportContext);
  // 使用 useChartFootBtn Hook 处理 tooltip footer 链接
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: ({ btnKey, dataAttrs }) => {
      if (btnKey === 'feeds' && dataAttrs.subcategory && dataAttrs.category) {
        toFeeds(dataAttrs.category, dataAttrs.subcategory);
      }
    },
  });

  // 创建 category -> config 映射
  const categoryConfigMap = useMemo(() => {
    return new Map(Object.entries(CATEGORY_CONFIG));
  }, []);

  const chartOptions = useMemo(() => {
    if (processedData.length === 0) return {};

    // 获取动态环比文案
    const { text: qoqText } = getFilterDateQOQText(filter);

    // 根据数据动态计算轴范围
    const xValues = processedData.map((d) => d.mentions).filter((v) => v > 0);
    const yValues = processedData
      .map((d) => d.avg_engagement_intensity)
      .filter((v) => v > 0);
    const xMin = Math.min(...xValues, 1);
    const xMax = Math.max(...xValues, 100);
    const yMin = Math.min(...yValues, 1);
    const yMax = Math.max(...yValues, 100);

    return {
      tooltip: {
        trigger: 'item',
        enterable: true,
        confine: true,
        // position: (point: any, params: any, dom: any, rect: any, size: any) =>
        //   DBO_EC.enterablePosition(point, params, dom, rect, size),
        formatter: (params: any) => {
          const d = params.data as IProcessedQuadrantItem;
          if (!d) return '';

          const topicName = isEn ? d.sub_category : d.sub_category_cn;
          const config = categoryConfigMap.get(d.category);
          const dotColor = config?.color || '#1890ff';
          const isPositive = d.change_rate >= 0;
          const changeValue = `${DBO_V.percentageFormat(
            d.change_rate * 100,
            '-',
            0,
          )}`;
          let changeClass = isPositive
            ? 'dbo-quadrant-tooltip__change--up'
            : 'dbo-quadrant-tooltip__change--down';
          if (changeValue === '-') {
            changeClass = '';
          }

          // 时间范围
          const currentRange = DBO_TM.timeTitle(filter);
          const prevRange = linkTime
            ? DBO_TM.timeTitle({
                ...linkTime,
                date_type: filter?.date_type || 'daily',
              })
            : '';

          return formatChartParamsDate({
            className: 'dbo-quadrant-tooltip',
            title: `<span class="dbo-quadrant-tooltip__title"><span class="dbo-quadrant-tooltip__dot" style="background:${dotColor}"></span>${topicName}</span>`,
            params,
            dealwithRecords: () => {
              const records: any[] = [
                {
                  col1: formatMessage({ id: 'Mentions' }),
                  col2: '',
                  col3: DBO_V.reviews(d.mentions),
                },
                {
                  col1: formatMessage({ id: 'Average Engagement' }),
                  col2: '',
                  col3: d.avg_engagement_intensity?.toFixed(1) || '-',
                },
              ];
              return records;
            },
            tablesConfigFunc: (records: any[]) => {
              const baseColumns = [
                { key: 'col1', header: '' },
                { key: 'col2', align: 'center', header: '' },
                { key: 'col3', align: 'right', weight: 'bold', header: '' },
              ];

              const tables: any[] = [
                {
                  showHeader: false,
                  records,
                  columns: baseColumns,
                },
              ];

              // 环比对比
              if (prevRange) {
                const vsRecords = [
                  {
                    col1: formatMessage({ id: qoqText }),
                    col2: '',
                    col3: `<span class="dbo-quadrant-tooltip__change ${changeClass}">${changeValue}</span>`,
                  },
                  {
                    col1: currentRange,
                    col2: '',
                    col3: DBO_V.reviews(d.mentions),
                  },
                  {
                    col1: '<div class="dbo-quadrant-tooltip__vs-border"></div>',
                    col2: `<span class="dbo-quadrant-tooltip__vs-label">VS</span>`,
                    col3: '<div class="dbo-quadrant-tooltip__vs-border"></div>',
                  },
                  {
                    col1: prevRange,
                    col2: '',
                    col3: DBO_V.reviews(d.previous_period_mentions),
                  },
                ];
                tables.push({
                  showHeader: true,
                  records: vsRecords,
                  columns: baseColumns,
                  className: 'dbo-quadrant-tooltip__table--vs',
                });
              }

              return tables;
            },
            footerText: getBottomClickBtn({
              category: d.category,
              subcategory: d.sub_category,
            }),
          });
        },
      },
      legend: {
        data: categoryList,
        bottom: 0,
        icon: 'circle',
        itemWidth: 8,
        itemHeight: 8,
        formatter: (name: string) => {
          if (isEn) {
            return name;
          }
          return categoryCnMap.get(name) || name;
        },
      },
      grid: {
        left: 40,
        right: 165,
        top: 20,
      },
      xAxis: {
        name: formatMessage({ id: 'Security-Related Mentions' }),
        type: 'log',
        logBase: 10,
        min: xMin,
        max: xMax,
        nameLocation: 'end',
        nameGap: 10,
        nameTextStyle: {
          color: '#262832',
          fontSize: 12,
        },
        axisLabel: {
          formatter: (value: number) => DBO_EC.reviews(value),
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#d9d9d9' },
        },
        splitLine: {
          lineStyle: { type: 'dashed', color: '#e8e8e8' },
        },
      },
      yAxis: {
        name: formatMessage({ id: 'Average Engagement' }),
        type: 'log',
        logBase: 10,
        min: yMin,
        max: DBO_EC.ceil(yMax * 1.5),
        nameLocation: 'middle',
        nameGap: 40,
        axisLabel: {
          formatter: (value: number) => DBO_EC.reviews(value),
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#d9d9d9' },
        },
        splitLine: {
          lineStyle: { type: 'dashed', color: '#e8e8e8' },
        },
        nameTextStyle: {
          color: '#262832',
          fontSize: 12,
        },
      },
      // 按 category 分组数据，每个类型一个 series，以支持 legend 过滤
      series: categoryList.map((category, index) => {
        const config = categoryConfigMap.get(category);
        const seriesData = processedData
          .filter((item) => item.category === category)
          .map((item) => ({
            value: [
              item.mentions,
              item.avg_engagement_intensity,
              item.sub_category,
              item.change_rate,
            ],
            name: item.sub_category,
            ...item,
          }));

        const series: any = {
          name: category,
          type: 'scatter',
          itemStyle: {
            color: config?.color || '#1890ff',
          },
          symbolSize: (val: any) => {
            const changeRate = val[3] || 0;
            return calculateBubbleSize(
              changeRate,
              BUBBLE_SIZE.min,
              BUBBLE_SIZE.max,
            );
          },
          // 气泡标签
          label: {
            show: true,
            position: 'right',
            distance: 5,
            formatter: (params: any) => {
              const d = params.data as IProcessedQuadrantItem;
              const name = isEn ? d.sub_category : d.sub_category_cn;
              const changeRate = d.change_rate || 0;
              const isPositive = changeRate >= 0;
              const iconKey = isPositive ? 'iconDown' : 'iconUp';
              const changeKey = isPositive ? 'changeUp' : 'changeDown';
              const changeRateText = DBO_V.percentageFormat(
                changeRate * 100,
                '-',
                0,
              );
              if (changeRate === 0) {
                return `{name|${name}}`;
              }
              return `{name|${name}} {${changeKey}|${changeRateText}}{${iconKey}|}`;
            },
            rich: {
              name: {
                color: '#262832',
                fontSize: 12,
              },
              changeUp: {
                color: '#52C41A',
                fontSize: 12,
              },
              changeDown: {
                color: '#F5222D',
                fontSize: 12,
              },
              iconDown: {
                width: 10,
                height: 10,
                backgroundColor: { image: iconUpGreen },
                verticalAlign: 'top',
              },
              iconUp: {
                width: 10,
                height: 10,
                backgroundColor: { image: iconDownRed },
                verticalAlign: 'middle',
              },
              verticalAlign: 'middle',
              borderWidth: 1,
              borderColor: '#000',
            },
          },
          labelLayout: {
            hideOverlap: true,
          },
          data: seriesData,
        };

        // markLine 只在第一个 series 上显示
        if (index === 0) {
          series.markLine = {
            silent: true,
            lineStyle: { type: 'dashed', color: '#999' },
            label: { show: false },
            data: [
              { yAxis: thresholds.y_axis, name: 'P50' },
              { xAxis: thresholds.x_axis, name: 'P60' },
            ],
          };
        }

        return series;
      }),
      // 四象限标签
      graphic: [
        // 左上：情绪放大(3)
        {
          type: 'text',
          left: 68,
          top: 20,
          z: 10,
          style: {
            text: formatMessage({ id: QUADRANT_CONFIG[3].label }),
            fill: QUADRANT_CONFIG[3].textColor,
            fontSize: 12,
            backgroundColor: QUADRANT_CONFIG[3].bgColor,
            padding: [6, 12],
            borderRadius: 10,
          },
        },
        // 右上：已发酵(2)
        {
          type: 'text',
          right: 165,
          top: 20,
          z: 10,
          style: {
            text: formatMessage({ id: QUADRANT_CONFIG[2].label }),
            fill: QUADRANT_CONFIG[2].textColor,
            fontSize: 12,
            backgroundColor: QUADRANT_CONFIG[2].bgColor,
            padding: [6, 12],
            borderRadius: 10,
          },
        },
        // 左下：稳定/噪音(4)
        {
          type: 'text',
          left: 68,
          bottom: 50,
          z: 10,
          style: {
            text: formatMessage({ id: QUADRANT_CONFIG[4].label }),
            fill: QUADRANT_CONFIG[4].textColor,
            fontSize: 12,
            backgroundColor: QUADRANT_CONFIG[4].bgColor,
            padding: [6, 12],
            borderRadius: 10,
          },
        },
        // 右下：扩散中(1)
        {
          type: 'text',
          right: 165,
          bottom: 50,
          z: 10,
          style: {
            text: formatMessage({ id: QUADRANT_CONFIG[1].label }),
            fill: QUADRANT_CONFIG[1].textColor,
            fontSize: 12,
            backgroundColor: QUADRANT_CONFIG[1].bgColor,
            padding: [6, 12],
            borderRadius: 10,
          },
        },
      ],
    };
  }, [
    processedData,
    thresholds,
    categoryList,
    categoryCnMap,
    formatMessage,
    filter,
    linkTime,
  ]);

  function rightTitle() {
    const list = [
      'Dot = Subtopic',
      'X-axis = Mentions',
      'Y-axis = Average Engagement (Mentions / Engagements)',
      'Color = Report Type',
    ];

    return `${list.map(formatMessage).join(' | ')} | ${formatMessage(
      'Bubble Size = |{id}|',
      {
        id: formatMessage(
          DBO_TM.qoqTxt(filter.date_type, filter.start_time, filter.end_time)
            .text,
        ),
      },
    )} ${formatMessage('↑ Increase ↓ Decrease')}`;
  }

  return (
    <div ref={divElementRef}>
      <EChartCommon
        options={chartOptions}
        height={height}
        rightTitle={rightTitle()}
      />
    </div>
  );
};

export default QuadrantScatterChart;
